import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { addAnswer } from '@/store/slices/chatbotFlow';
import { DatePicker, Button, Tooltip } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styles from './styles/date-input.module.css';
import sharedStyles from './styles/shared-components.module.css';

const dateFormats = {
  US: 'MM/DD/YYYY',
  UK: 'DD/MM/YYYY',
  EU: 'YYYY/MM/DD',
};

const DateInput = ({ addAnswer, block, isChatbotVisible, animTransition }) => {
  const { i18n } = useTranslation(); 
  const [selectedDates, setSelectedDates] = useState([]);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const getDateFormat = () => {
    const locale = i18n.language; 

    if (locale === 'en-US') {
      return dateFormats.US;
    }

    if (locale === 'en-GB') {
      return dateFormats.UK;
    }

    return dateFormats.EU;
  };

  const handleSingleDateChange = useCallback(
    (date, dateString) => {
      const { options } = block;
      let updatedDates = [];

      if (
        options.type === 'MULTIPLE' ||
        options.type === 'FREELANCE' ||
        options.type === 'FREE'
      ) {
        updatedDates = [...selectedDates, ...date]?.filter(
          (d) => d && dayjs(d).isValid()
        );
      } else {
        updatedDates = [date]?.filter((d) => d && dayjs(d).isValid());
      }

      setSelectedDates(updatedDates);
      setShowError(false);
      setErrorMessage('');
    },
    [block, selectedDates]
  );

  const handleMultipleDateChange = useCallback(
    (dates, dateStrings) => {
      const updatedDates = dates?.filter((d) => d && dayjs(d).isValid());

      setSelectedDates(updatedDates);
      setShowError(false);
      setErrorMessage('');
    },
    []
  );

  const submitInput = useCallback(() => {
    const dateFormat = getDateFormat()
    
    if (selectedDates?.length > 0) {
      const formattedDates = selectedDates.map((day) =>
        dayjs(day).format(dateFormat)
      );
      const formattedDatesForBackend = selectedDates.map((day) =>
        dayjs(day).format(dateFormats.US)
      );
      addAnswer({type: 'date', formattedDates, formattedDatesForBackend});
      setSelectedDates([]);
    } else {
      setShowError(true);
      setErrorMessage('Please select at least one date.');
    }
  }, [selectedDates, addAnswer]);

  const getConfig = useCallback(() => {
    let config;
    const { options } = block;
    switch (options?.type) {
      case 'EXACT':
        config = {
          onChange: handleSingleDateChange,
        };
        break;
      case 'MULTIPLE':
        config = {
          multiple: true,
          onChange: handleMultipleDateChange,
        };
        break;
      case 'FREE':
        config = {
          multiple: true,
          onChange: handleMultipleDateChange,
        };
        break;
      default:
        config = {
          onChange: handleSingleDateChange,
        };
        break;
    }

    // BirthDay correction
    if (
      block.dataType === 'CANDIDATE_BIRTHDAY' ||
      block.dataType === 'CLIENT_BIRTHDAY'
    ) {
      config['disabledDate'] = (current) =>
        current && current.isAfter(dayjs(), 'day');
    }

    return config;
  }, [block, handleSingleDateChange, handleMultipleDateChange]);

  const datePickerConfig = getConfig();

  return (
    <div className={sharedStyles.wrapper}>
      <div className={styles.input_container}>
        <DatePicker
          className={`custom-date-picker ${styles.input} ${styles.date_picker}`}
          variant="borderless"
          {...datePickerConfig}
          format={getDateFormat()} 
        />
      </div>

      <Tooltip
        placement="topRight"
        align={{ offset: [-5, -5] }}
        title={errorMessage}
        open={showError && isChatbotVisible && animTransition}
      >
        <Button
          className={sharedStyles.send_button}
          type="text"
          danger={showError}
          onClick={submitInput}
          icon={<SendOutlined style={{ fontSize: '24px' }} />}
        />
      </Tooltip>
    </div>
  );
};

DateInput.propTypes = {
  addAnswer: PropTypes.func.isRequired,
  block: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  isChatbotVisible: state.chatbotVisibility.isVisible,
  animTransition: state.chatbotVisibility.animTransition,
});

const mapDispatchToProps = { addAnswer };

export default connect(mapStateToProps, mapDispatchToProps)(DateInput);
